import { FC, useCallback, useMemo, useState } from "react";

import {
  AddBankDetailsBanner,
  GetVerifiedBanner,
  GetVerifiedOnmetaBanner,
  // PaymentRequestLimitsBanner,
} from "../../components/banners";
import { Button, CollapsePanel, Icon } from "../../components/common";
import { PaymentRequestList } from "../../components/lists";
import { NewBankAccountModal } from "../../components/modals";
import { WalletDropdown } from "../../components/wallet";
import { ROUTES } from "../../routes";
import {
  useGetPaymentRequestLinkQuery,
  useUpdatePaymentRequestLinkMutation,
} from "../../store/services/paymentRequestLinks";
import { Currency } from "../../types/InvoiceTypes";
import { useGetWalletsQuery } from "../../store/services/wallets";
import { Chain, OnmetaStatus, Wallet } from "../../types/WalletTypes";
import { copyTextToClipboard } from "../../utils/actions/clipboard";
// import { formatDate, DateFormat } from "../../utils/date";
import { usePolling } from "../../utils/hooks/usePolling";
import { isMobileMedia } from "../../utils/media";
import { toastSuccess } from "../../utils/toasts";
import { track } from "../../utils/tracking";
import { useSelector } from "react-redux";
import { selectCurrentUser } from "../../store/reducers/user";
import { KycProvider } from "../../types/UserTypes";
import { UsdAccountBanner } from "../../components/banners/UsdAccountBanner";
import {
  FeatureHighlights,
  // TestimonialSection,
  TransferCalculator,
} from "../../components/transfers";
import TestLayout from "../../layouts/Test.layout";
import classNames from "classnames";

export const PaymentRequestPage: FC = () => {
  const {
    allowed_incoming_currencies,
    allowed_outgoing_currencies,
    kyc_complete_and_approved,
    is_onmeta_eligible,
    onmeta_kyc_complete_and_approved,
    primary_kyc_provider,
    is_usd_eligible,
    usd_kyc_complete_and_approved,
    ga_id,
  } = useSelector(selectCurrentUser);

  const { data: paymentRequestLink } = useGetPaymentRequestLinkQuery();
  const [updatePaymentRequestLink] = useUpdatePaymentRequestLinkMutation();
  const { data: walletsResponse, refetch } = useGetWalletsQuery();
  const [showBankAccountForm, setShowBankAccountForm] = useState(false);
  const [showKYCModal, setShowKYCModal] = useState(false);
  const [showOnmetaKYCModal, setShowOnmetaKYCModal] = useState(false);
  const [selectedCurrency, setSelectedCurrency] = useState<Currency>(
    Currency.EUR
  );

  const wallets = walletsResponse?.results || [];
  const isVerified = kyc_complete_and_approved;
  const isOnmetaVerified = onmeta_kyc_complete_and_approved;
  const shouldShowUsdBanner = is_usd_eligible && !usd_kyc_complete_and_approved;

  const fiatWallets = wallets.filter(
    (wallet) => !wallet.address && wallet.chain === Chain.INR
  );
  const hasWallets = fiatWallets.length > 0;

  const selectedWallet = fiatWallets?.find(
    (wallet) => wallet.id === paymentRequestLink?.wallet_id
  );
  const createOrShareText =
    isVerified && !!selectedWallet ? "Share your" : "Create a";

  usePolling(
    refetch,
    5000,
    selectedWallet?.onmeta_status === OnmetaStatus.PENDING
  );

  const route = ROUTES.PAYMENT_REQUEST_PUBLIC.replace(
    ":id",
    paymentRequestLink?.public_id || ""
  );

  const onSelectWallet = (wallet: Wallet) => {
    updatePaymentRequestLink({ wallet_id: wallet.id! });
  };

  const onClickShare = useCallback(() => {
    const title = "Hey! Could you please send me money via this link?";
    const url = `${window.location.origin}${route}`;

    const hasShareApi =
      isMobileMedia() && typeof navigator.share === "function";

    // Track share event
    track("share_payment_link", {
      event_category: "engagement",
      event_label: "payment_request",
      share_method: hasShareApi ? "native_share" : "copy_link",
      user_id: ga_id,
      uid: ga_id,
    });

    if (hasShareApi) {
      navigator.share({
        title,
        url,
      });
    } else {
      copyTextToClipboard(`${title}: ${url}`);
      toastSuccess("Share link copied!");
    }
  }, [route, ga_id]);

  const calculatorCTALabel = useMemo(() => {
    if (!isVerified) {
      return "Get Verified to Receive Money";
    }

    if (
      is_onmeta_eligible &&
      !isOnmetaVerified &&
      primary_kyc_provider !== KycProvider.ONMETA
    ) {
      return "Verify with Onmeta to Receive INR";
    }

    if (!hasWallets) {
      return "Add Bank Details to Receive Money";
    }

    return "Share Payment Link";
  }, [
    hasWallets,
    isOnmetaVerified,
    isVerified,
    is_onmeta_eligible,
    primary_kyc_provider,
  ]);

  const onClickCalculatorCTA = useCallback(() => {
    if (!isVerified) {
      setShowKYCModal(true);
      return;
    }

    if (
      is_onmeta_eligible &&
      !isOnmetaVerified &&
      primary_kyc_provider !== KycProvider.ONMETA
    ) {
      setShowOnmetaKYCModal(true);
      return;
    }

    if (!hasWallets) {
      setShowBankAccountForm(true);
      return;
    }

    onClickShare();
  }, [
    hasWallets,
    isOnmetaVerified,
    isVerified,
    is_onmeta_eligible,
    primary_kyc_provider,
    onClickShare,
  ]);

  //const disabledAction = false;
  // verbose hacky logic to disable share button but it works :-)
  const disabledAction = useMemo(() => {
    if (!isVerified) {
      return false;
    }

    if (
      is_onmeta_eligible &&
      !isOnmetaVerified &&
      primary_kyc_provider !== KycProvider.ONMETA
    ) {
      return false;
    }

    if (!hasWallets) {
      return false;
    }
    if (
      selectedWallet &&
      selectedWallet?.onmeta_status !== OnmetaStatus.SUCCESS
    ) {
      return true;
    }

    return false;
  }, [
    hasWallets,
    isOnmetaVerified,
    isVerified,
    is_onmeta_eligible,
    primary_kyc_provider,
    selectedWallet,
  ]);

  return (
    <TestLayout
      title="Personal transfers"
      containerClassName="flex flex-col !p-0 md:items-center"
      onSelectWallet={onSelectWallet}
    >
      <FeatureHighlights />
      <div className="flex flex-col gap-6 p-4 md:w-2/3">
        <div className="flex flex-col px-4 pt-4 pb-6 bg-purple-50 rounded-3xl gap-1">
          <div className="flex flex-col pb-3">
            {/* <span className="font-extrabold">Transfer calculator</span> */}
            <span className="font-semibold text-violet-300">
              {createOrShareText} reusable link to receive money from Europe,
              the UK or USA.
            </span>
            <span className="font-semibold text-violet-300">
              €5 min -&gt; €5000 max
            </span>
          </div>

          <TransferCalculator
            allowedIncomingCurrencies={allowed_incoming_currencies}
            allowedOutgoingCurrencies={allowed_outgoing_currencies}
            priceData={paymentRequestLink}
            onChange={(input) => {
              if (input.currency) {
                setSelectedCurrency(input.currency);
              }
            }}
          />
          <h5 className="font-manrope font-extrabold text-base mt-2">
            INR is sent to
          </h5>
          <WalletDropdown
            fullWidth={true}
            values={fiatWallets}
            value={selectedWallet}
            type="fiat"
            onSelect={onSelectWallet}
            onCreate={() => setShowBankAccountForm(true)}
          />

          <a
            href={`https://www.xe.com/currencyconverter/convert/?Amount=1&From=${selectedCurrency.toUpperCase()}&To=INR`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex flex-wrap gap-2 items-start self-stretch px-6 py-4 text-base font-semibold rounded-2xl border border-solid bg-white bg-opacity-50 border-[color:var(--Violet-violet-50,#E8E9ED)] max-md:px-5 cursor-pointer hover:bg-opacity-75"
          >
            <div className="flex-1 shrink text-gray-500 basis-0 max-md:max-w-full">
              Get the best available rate on the market
            </div>
            <div className="text-right text-indigo-950">Compare →</div>
          </a>
        </div>
        <Button
          className={"!rounded-2xl !justify-start"}
          contentClassName={classNames("text-left", {
            "text-white": !disabledAction,
          })}
          disabled={disabledAction}
          onClick={onClickCalculatorCTA}
          variant={Button.variant.TEMPTING}
        >
          {calculatorCTALabel} <span className="mr-2"></span>
          <Icon icon="Send" size={24} />
        </Button>
      </div>
      <div className="flex flex-col gap-6 p-4 md:w-full">
        {(!hasWallets ||
          !isVerified ||
          !isOnmetaVerified ||
          (!hasWallets && shouldShowUsdBanner)) && (
          <div className="pl-4 flex flex-col gap-3 max-w-full">
            <h5 className="font-poppins font-bold text-xl">
              Get started with Archway
            </h5>

            <div className="flex overflow-x-auto md:max-w-full md:flex-wrap w-full">
              <div className="flex gap-6 pr-4">
                {!isVerified && (
                  <GetVerifiedBanner
                    show={showKYCModal}
                    setShow={setShowKYCModal}
                  />
                )}

                {is_onmeta_eligible &&
                  !isOnmetaVerified &&
                  primary_kyc_provider !== KycProvider.ONMETA && (
                    <GetVerifiedOnmetaBanner
                      show={showOnmetaKYCModal}
                      setShow={setShowOnmetaKYCModal}
                    />
                  )}

                {!hasWallets && (
                  <AddBankDetailsBanner
                    show={showBankAccountForm}
                    setShow={setShowBankAccountForm}
                  />
                )}
                {!hasWallets && shouldShowUsdBanner && <UsdAccountBanner />}
              </div>
            </div>
          </div>
        )}

        {hasWallets && (
          <>
            {/* <div className="px-4 flex flex-col gap-3">
              <h5 className="font-manrope font-extrabold text-base">
                INR is sent to
              </h5>

              <WalletDropdown
                values={fiatWallets}
                value={selectedWallet}
                type="fiat"
                onSelect={onSelectWallet}
                onCreate={() => setShowBankAccountForm(true)}
              />
            </div> */}
            {/* <PaymentRequestLimitsBanner
              currency={Currency.EUR}
              limit={paymentRequestLink?.eur_monthly_limit || 0}
              received={paymentRequestLink?.month_eur_total || 0}
              month={formatDate(new Date().toString(), DateFormat.MMMM)}
            /> */}
          </>
        )}

        <PaymentRequestList />
        {hasWallets && shouldShowUsdBanner && (
          <div className="pl-4 flex flex-col gap-3">
            <UsdAccountBanner />
          </div>
        )}

        {/* <TestimonialSection testimonials={mockTestimonials} /> */}

        <CollapsePanel
          title="How Transfers work..."
          className="py-4 px-4 text-xs"
          open={true}
        >
          For our Indian users! 🌟 Easily receive money in your INR account in
          India from Europe, Great Britain or USA with excellent FX rates:
          <br />
          <br />
          1. <strong>Verify Your Identity</strong> with our trusted Indian
          partner.
          <br />
          2. <strong>Add Your INR Account</strong> (the name should closely
          match your official ID).
          <br />
          3. <strong>Create your Payment Link</strong> and share it with your
          payers (<b>or use it yourself</b>).
          <br />
          4. <strong>Get Paid Fast</strong>—money will arrive in your INR
          account within just a few hours!
          <br />
          <br />
          If you have a bank account in <b>Europe, the UK or USA</b> and an INR
          account in India, this is the ideal solution to transfer money to
          yourself. 😊
        </CollapsePanel>
      </div>

      <NewBankAccountModal
        isOpen={showBankAccountForm}
        close={() => setShowBankAccountForm(false)}
        callback={() => {
          // toastSuccess("Bank account created successfully");
          setShowBankAccountForm(false);
        }}
      />
    </TestLayout>
  );
};
